// Data model (§5). All coordinates are normalized to the oriented source image (0..1 on each axis,
// before any crop or geometry), so settings sync across photos of different sizes.
import type { Pt } from '../color/curve';
import type { HueBand } from '../color/hsl';

export type { Pt, HueBand };

export type AspectId = 'free' | 'original' | '1:1' | '4:5' | '3:2' | '2:3' | '16:9' | '9:16' | '4:3';

export type StrokePoint = { x: number; y: number; p: number };
export type Stroke = { points: StrokePoint[]; size: number; feather: number; flow: number; erase: boolean };

export type LinearGeom = { x0: number; y0: number; x1: number; y1: number };
export type RadialGeom = { cx: number; cy: number; rx: number; ry: number; angle: number; feather: number };
export type MaskAdj = {
  exposure: number;
  contrast: number;
  temp: number;
  tint: number;
  saturation: number;
  highlights: number;
  shadows: number;
  /** M22: hue shift (−100..100 = −45°..+45° in OkLCh), vibrance (−100..100), clarity (−100..100). */
  hue: number;
  vibrance: number;
  clarity: number;
};
/** M22: an area a colour / object mask is limited to (a linear or radial gradient, multiplied in). */
export type MaskLimit = { kind: 'linear'; geom: LinearGeom } | { kind: 'radial'; geom: RadialGeom };
/** M22: a tapped colour sample (source-normalized point; its colour is read from the photo). */
export type ColourSample = { x: number; y: number };
export type ColourPreset = 'tomato' | 'greens' | 'cheese' | 'meat' | 'bread';
/** M22 colour-range mask: pixels near the sampled colours (or a preset's hue range) in OkLCh. */
export type ColourGeom = {
  samples: ColourSample[];
  /** A preset's colour range, used together with any tapped samples. */
  preset?: ColourPreset;
  /** 0..100: how far from the sampled colour still counts. */
  range: number;
  /** 0..100: how gradual the edge of the range is. */
  softness: number;
  /** Brush refine: paint in (erase false) / paint out (erase true). */
  strokes: Stroke[];
  limit?: MaskLimit;
};
/** M22 smart object pick: points on the object (add) or off it (add false), segmented on-device. */
export type ObjectPoint = { x: number; y: number; add: boolean };
export type ObjectGeom = {
  points: ObjectPoint[];
  /** 0..100: edge refine (guided filter strength). */
  edge: number;
  strokes: Stroke[];
  limit?: MaskLimit;
};
export type LocalMask = {
  id: string;
  kind: 'linear' | 'radial' | 'brush' | 'colour' | 'object';
  geom: LinearGeom | RadialGeom | { strokes: Stroke[] } | ColourGeom | ObjectGeom;
  invert: boolean;
  adj: MaskAdj;
};
export type HealSpot = {
  kind: 'heal' | 'clone';
  stroke: Stroke;
  offset: { x: number; y: number };
  opacity: number;
};

export type Rgb = [number, number, number];

export type Effects = {
  vignette: { amount: number /*-100..100*/; midpoint: number /*0..100*/; feather: number /*0..100*/ };
  grain: { amount: number /*0..100*/; size: number /*0..100*/ };
  splitTone: {
    shadowHue: number /*0..360*/;
    shadowSat: number /*0..100*/;
    highlightHue: number;
    highlightSat: number;
    balance: number /*-100..100*/;
  };
};

export type BackgroundSettings = {
  mode: 'off' | 'auto' | 'key';
  key?: { color: Rgb; tolerance: number; softness: number; connected?: boolean };
  refine: Stroke[];
  edge: { feather: number; shift: number; decontaminate: number };
  fill: { kind: 'transparent' | 'color'; color: Rgb };
  shadow: { enabled: boolean; opacity: number; blur: number; offsetY: number };
  maskCacheKey?: string;
};

export type UpscaleSettings = {
  /** 1 = off. The output is capped at 24 MP (M13). */
  factor: 1 | 2 | 4;
  model: 'general' | 'plus';
  /** 0..100. */
  strength: number;
};

export type Settings = {
  v: 4;
  light: {
    exposure: number;
    contrast: number;
    highlights: number;
    shadows: number;
    whites: number;
    blacks: number;
  };
  wb: { temp: number; tint: number };
  color: { vibrance: number; saturation: number };
  hsl: Record<HueBand, { h: number; s: number; l: number }>;
  curve: { master: Pt[]; r: Pt[]; g: Pt[]; b: Pt[] };
  detail: { sharpAmount: number; sharpRadius: number; sharpMasking: number; nrLuma: number; nrColor: number };
  geometry: {
    crop: { x: number; y: number; w: number; h: number };
    aspect: AspectId | null;
    angle: number;
    vertical: number;
    horizontal: number;
    constrain: boolean;
  };
  masks: LocalMask[];
  heal: HealSpot[];
  skin: { amount: number; strokes: Stroke[] };
  cleanWhites: { amount: number; strokes: Stroke[] | null };
  /** §11.19 effects (schema v2): vignette after geometry, grain at output, split toning after Color. */
  effects: Effects;
  lut: { id: string | null; intensity: number };
  background: BackgroundSettings;
  /** M13 (schema v3): the upscaled pixels replace the source for the viewer and every export. */
  upscale: UpscaleSettings;
};

export type PhotoMeta = {
  id: string;
  name: string;
  size: number;
  lastModified: number;
  width: number;
  height: number;
  orientation: number;
  rating: 0 | 1 | 2 | 3 | 4 | 5;
  rejected: boolean;
  hasSidecar: boolean;
};

export type Sidecar = {
  schema: 4;
  app: 'eat-photo-booth';
  appVersion: string;
  source: { name: string; size: number; lastModified: number };
  settings: Settings;
  rating: number;
  rejected: boolean;
  updatedAt: string;
};

export const SYNC_GROUPS = [
  'light',
  'wb',
  'color',
  'hsl',
  'curve',
  'detail',
  'crop',
  'perspective',
  'masks',
  'heal',
  'skin',
  'cleanWhites',
  'lut',
  'effects',
  'background',
] as const;
export type SyncGroup = (typeof SYNC_GROUPS)[number];

export type Preset = {
  id: string;
  name: string;
  nameEs?: string;
  builtIn: boolean;
  groups: SyncGroup[];
  settings: Partial<Settings>;
};

export type ExportPreset = {
  id: string;
  name: string;
  nameEs?: string;
  builtIn: boolean;
  format: 'jpeg' | 'png' | 'tiff';
  bitDepth: 8 | 16;
  quality: number;
  size: {
    mode: 'original' | 'longEdge' | 'width' | 'height' | 'exact';
    w?: number;
    h?: number;
    fit?: 'contain' | 'cover';
  };
  dpi: number;
  colorSpace: 'srgb' | 'p3' | 'adobergb';
  sharpen: { target: 'none' | 'screen' | 'matte' | 'glossy'; amount: 'low' | 'standard' | 'high' };
  background: 'asEdited' | 'forceTransparent' | 'forceColor';
  watermark: boolean;
  metadata: 'keepAll' | 'stripGps' | 'copyrightOnly' | 'stripAll';
  rename: { enabled: boolean; template: string };
  subfolder: string;
  /** Free-text note shown in the dialog, e.g. "check the app's current spec" (D7). */
  note?: string;
};
